import React from 'react';
import Icon from '../../../components/AppIcon';

const MarketInsights = ({ craneData }) => {
  // Mock market insights data based on crane type
  const generateMarketData = () => {
    const craneType = craneData?.craneType || 'Mobile Crane';
    
    const marketData = {
      'Mobile Crane': {
        demandTrend: 'Strong',
        inventoryLevel: 'Low',
        priceMovement: '+3.2%',
        avgDaysOnMarket: 45,
        topRegions: ['Texas', 'California', 'Florida'],
        seasonalPeak: 'Spring/Summer'
      },
      'Rough Terrain': {
        demandTrend: 'Moderate',
        inventoryLevel: 'Adequate',
        priceMovement: '+1.8%',
        avgDaysOnMarket: 52,
        topRegions: ['Texas', 'Louisiana', 'North Dakota'],
        seasonalPeak: 'Spring'
      },
      'All Terrain': {
        demandTrend: 'High',
        inventoryLevel: 'Limited',
        priceMovement: '+4.5%',
        avgDaysOnMarket: 38,
        topRegions: ['New York', 'California', 'Illinois'],
        seasonalPeak: 'Year-round'
      },
      'Crawler Crane': {
        demandTrend: 'Very High',
        inventoryLevel: 'Very Low',
        priceMovement: '+6.1%',
        avgDaysOnMarket: 32,
        topRegions: ['Texas', 'Louisiana', 'Pennsylvania'],
        seasonalPeak: 'Spring/Summer'
      }
    };

    return marketData?.[craneType] || marketData?.['Mobile Crane'];
  };

  const marketData = generateMarketData();

  const getStatusColor = (status) => {
    const colors = {
      'Very High': 'text-green-600 bg-green-100',
      'High': 'text-green-600 bg-green-100',
      'Strong': 'text-green-600 bg-green-100',
      'Moderate': 'text-yellow-600 bg-yellow-100',
      'Low': 'text-red-600 bg-red-100',
      'Very Low': 'text-red-600 bg-red-100',
      'Limited': 'text-green-600 bg-green-100',
      'Adequate': 'text-yellow-600 bg-yellow-100'
    };
    return colors?.[status] || 'text-gray-600 bg-gray-100';
  };

  const recentSales = [
    {
      make: craneData?.make || 'Liebherr',
      model: 'Similar Model',
      year: (craneData?.year || 2020) - 1,
      price: 875000,
      location: 'Houston, TX',
      daysAgo: 12
    },
    {
      make: craneData?.make || 'Grove',
      model: 'Comparable Unit',
      year: (craneData?.year || 2020),
      price: 920000,
      location: 'Atlanta, GA',
      daysAgo: 28
    },
    {
      make: 'Terex',
      model: 'Similar Capacity',
      year: (craneData?.year || 2020) - 2,
      price: 685000,
      location: 'Denver, CO',
      daysAgo: 45
    }
  ];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })?.format(amount);
  };

  return (
    <div className="space-y-6">
      {/* Market Overview */}
      <div className="bg-surface border border-border rounded-lg shadow-card p-4">
        <h3 className="font-medium text-text-primary mb-4 flex items-center space-x-2">
          <Icon name="TrendingUp" size={16} className="text-primary" />
          <span>Market Overview</span>
        </h3>
        
        <div className="space-y-3">
          <div className="flex items-center justify-between p-2 bg-muted rounded">
            <span className="text-sm text-text-secondary">Demand Level</span>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(marketData?.demandTrend)}`}>
              {marketData?.demandTrend}
            </span>
          </div>
          
          <div className="flex items-center justify-between p-2 bg-muted rounded">
            <span className="text-sm text-text-secondary">Inventory</span>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(marketData?.inventoryLevel)}`}>
              {marketData?.inventoryLevel}
            </span>
          </div>
          
          <div className="flex items-center justify-between p-2 bg-muted rounded">
            <span className="text-sm text-text-secondary">Price Movement</span>
            <span className="text-sm font-medium text-success">{marketData?.priceMovement}</span>
          </div>
          
          <div className="flex items-center justify-between p-2 bg-muted rounded">
            <span className="text-sm text-text-secondary">Avg Days on Market</span>
            <span className="text-sm font-medium text-text-primary">{marketData?.avgDaysOnMarket} days</span>
          </div>
        </div>
      </div>
      {/* Top Markets */}
      <div className="bg-surface border border-border rounded-lg shadow-card p-4">
        <h3 className="font-medium text-text-primary mb-4 flex items-center space-x-2">
          <Icon name="MapPin" size={16} className="text-accent" />
          <span>Top Markets</span>
        </h3>
        
        <div className="space-y-2">
          {marketData?.topRegions?.map((region, index) => (
            <div key={index} className="flex items-center justify-between p-2 bg-muted rounded">
              <div className="flex items-center space-x-2">
                <div className={`w-2 h-2 rounded-full ${index === 0 ? 'bg-green-500' : index === 1 ? 'bg-yellow-500' : 'bg-orange-500'}`} />
                <span className="text-sm text-text-primary">{region}</span>
              </div>
              <span className="text-xs text-text-secondary">#{index + 1}</span>
            </div>
          ))}
        </div>
        
        <div className="mt-3 p-2 bg-blue-50 rounded text-xs text-blue-800">
          <Icon name="Info" size={12} className="inline mr-1" />
          Seasonal peak: {marketData?.seasonalPeak}
        </div>
      </div>
      {/* Recent Sales */}
      <div className="bg-surface border border-border rounded-lg shadow-card p-4">
        <h3 className="font-medium text-text-primary mb-4 flex items-center space-x-2">
          <Icon name="DollarSign" size={16} className="text-success" />
          <span>Recent Sales</span>
        </h3>
        
        <div className="space-y-3">
          {recentSales?.map((sale, index) => (
            <div key={index} className="p-3 bg-muted rounded-lg">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-text-primary">
                  {sale?.make} {sale?.model}
                </span>
                <span className="text-sm font-semibold text-success">
                  {formatCurrency(sale?.price)}
                </span>
              </div>
              <div className="flex items-center justify-between text-xs text-text-secondary">
                <span>{sale?.year} • {sale?.location}</span>
                <span>{sale?.daysAgo} days ago</span>
              </div>
            </div>
          ))}
        </div>
        
        <div className="mt-3 text-xs text-text-secondary">
          <Icon name="Clock" size={12} className="inline mr-1" />
          Updated daily from verified sales data
        </div>
      </div>
      {/* Market Alerts */}
      <div className="bg-surface border border-border rounded-lg shadow-card p-4">
        <h3 className="font-medium text-text-primary mb-4 flex items-center space-x-2">
          <Icon name="Bell" size={16} className="text-warning" />
          <span>Market Alerts</span>
        </h3>
        
        <div className="space-y-3">
          <div className="p-3 bg-green-50 rounded-lg border border-green-200">
            <div className="flex items-start space-x-2">
              <Icon name="TrendingUp" size={16} className="text-green-600 mt-0.5" />
              <div>
                <div className="text-sm font-medium text-green-800">Strong Demand Alert</div>
                <div className="text-xs text-green-700">
                  {craneData?.craneType || 'Mobile cranes'} in your capacity range seeing 15% above average pricing
                </div>
              </div>
            </div>
          </div>
          
          <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-start space-x-2">
              <Icon name="Calendar" size={16} className="text-blue-600 mt-0.5" />
              <div>
                <div className="text-sm font-medium text-blue-800">Seasonal Opportunity</div>
                <div className="text-xs text-blue-700">
                  Spring selling season approaching - historically best time for {craneData?.craneType || 'crane'} sales
                </div>
              </div>
            </div>
          </div>
          
          <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
            <div className="flex items-start space-x-2">
              <Icon name="Package" size={16} className="text-amber-600 mt-0.5" />
              <div>
                <div className="text-sm font-medium text-amber-800">Supply Update</div>
                <div className="text-xs text-amber-700">
                  New equipment lead times extending - supporting used equipment values
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Market Tips */}
      <div className="bg-surface border border-border rounded-lg shadow-card p-4">
        <h3 className="font-medium text-text-primary mb-4 flex items-center space-x-2">
          <Icon name="Lightbulb" size={16} className="text-accent" />
          <span>Selling Tips</span>
        </h3>
        
        <div className="space-y-2 text-sm">
          <div className="flex items-start space-x-2">
            <Icon name="CheckCircle" size={14} className="text-success mt-0.5" />
            <span className="text-text-primary">Complete maintenance records add 5-8% to value</span>
          </div>
          <div className="flex items-start space-x-2">
            <Icon name="CheckCircle" size={14} className="text-success mt-0.5" />
            <span className="text-text-primary">Professional photos increase inquiry rates by 40%</span>
          </div>
          <div className="flex items-start space-x-2">
            <Icon name="CheckCircle" size={14} className="text-success mt-0.5" />
            <span className="text-text-primary">Pricing within 5% of market value reduces selling time</span>
          </div>
          <div className="flex items-start space-x-2">
            <Icon name="CheckCircle" size={14} className="text-success mt-0.5" />
            <span className="text-text-primary">Flexible financing options expand buyer pool</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketInsights;